import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Table, { Column } from '~/components/Table';
import { CARRIERS_WITH_GST } from '~/constants/carriers';
import { currencies } from '~/constants/currency';
import { colors, fontWeight } from '~/styles/constants';
import Invoice from '~/types/Invoice';
import InvoiceItem from '~/types/InvoiceItem';
import { InjectedTranslateProps, translate } from '~/utils/translate';

const commonTableHeaderCellStyle = css`
  color: ${colors.black};
  padding: 0.625rem;
  font-size: 0.8125rem;
  line-height: 1.125rem;
`;

const TableHeaderContent = styled.div`
  ${commonTableHeaderCellStyle}
  font-size: 0.875rem;
  font-weight: ${fontWeight.medium};
`;

const TableCellContent = styled.div`
  ${commonTableHeaderCellStyle}
  font-weight: ${fontWeight.light};
`;

const noPaddingStyles = css`
  padding: 0;

  :last-of-type {
    padding-right: 0;
  }
  :first-of-type {
    padding-left: 0;
  }
`;

const rightAlignNoPaddingStyles = css`
  ${noPaddingStyles}
  text-align: right;
`;

const StyledFooterRow = styled.tr`
  :not(:last-of-type) {
    border-bottom: 1px solid ${colors.gray50};
  }
`;

const StyledFooterCell = styled.td`
  ${commonTableHeaderCellStyle}
  font-size: 0.875rem;
  text-align: right;
`;

const OverflowTableCell = styled(TableCellContent)`
  overflow-wrap: anywhere;
`;

interface InvoiceItemsTableProps {
  invoiceItems: InvoiceItem[];
  invoice: Invoice;
  t;
}

const InvoiceItemsTable = (props: InvoiceItemsTableProps & InjectedTranslateProps) => {
  const { invoiceItems, invoice, t } = props;

  const columns: Column<InvoiceItem>[] = [
    // Date
    {
      header: () => (
        <TableHeaderContent>
          {t('settings:account.billing.invoices.download.invoiceItemsTable.headers.date')}
        </TableHeaderContent>
      ),
      cell: ({ data }: { data: InvoiceItem }) => (
        <TableCellContent>{data.objectCreated.format('l')}</TableCellContent>
      ),
      customHeaderStyles: noPaddingStyles,
      customCellStyles: noPaddingStyles,
      noPadding: true,
    },
    // Item Type
    {
      header: () => (
        <TableHeaderContent>
          {t('settings:account.billing.invoices.download.invoiceItemsTable.headers.itemType')}
        </TableHeaderContent>
      ),
      cell: ({ data }: { data: InvoiceItem }) => (
        <TableCellContent>{data.invoiceItemType}</TableCellContent>
      ),
      customHeaderStyles: noPaddingStyles,
      customCellStyles: noPaddingStyles,
      noPadding: true,
    },
    // Carrier
    {
      header: () => (
        <TableHeaderContent>
          {t('settings:account.billing.invoices.download.invoiceItemsTable.headers.carrier')}
        </TableHeaderContent>
      ),
      cell: ({ data }: { data: InvoiceItem }) => (
        <TableCellContent>{data.carrier}</TableCellContent>
      ),
      customHeaderStyles: noPaddingStyles,
      customCellStyles: noPaddingStyles,
      noPadding: true,
    },
    // Tracking Number
    {
      header: () => (
        <TableHeaderContent>
          {t('settings:account.billing.invoices.download.invoiceItemsTable.headers.trackingNumber')}
        </TableHeaderContent>
      ),
      cell: ({ data }: { data: InvoiceItem }) => (
        <OverflowTableCell>
          {`${data.trackingNumber || ''} ${data.description ? `(${data.description})` : ''} ${
            data.objectPurpose === 'REFUND' ? '(refund)' : ''
          }`}
        </OverflowTableCell>
      ),
      customHeaderStyles: noPaddingStyles,
      customCellStyles: noPaddingStyles,
      noPadding: true,
    },
    // Tax
    {
      header: () => (
        <TableHeaderContent>
          {t('settings:account.billing.invoices.download.invoiceItemsTable.headers.tax')}
        </TableHeaderContent>
      ),
      cell: ({ data }: { data: InvoiceItem }) => (
        <TableCellContent>
          {CARRIERS_WITH_GST.some((carrier) => carrier.name === data.carrier) &&
            t('settings:account.billing.invoices.download.invoiceItemsTable.gstIncluded')}
        </TableCellContent>
      ),
      customCellStyles: rightAlignNoPaddingStyles,
      customHeaderStyles: rightAlignNoPaddingStyles,
      noPadding: true,
    },
    // Amount
    {
      header: () => (
        <TableHeaderContent>
          {t('settings:account.billing.invoices.download.invoiceItemsTable.headers.amount')}
        </TableHeaderContent>
      ),
      cell: ({ data }: { data: InvoiceItem }) => (
        <TableCellContent>
          {t('translations:currency', {
            value: { value: data.amount, currency_iso: data.currency },
          })}
        </TableCellContent>
      ),
      customCellStyles: rightAlignNoPaddingStyles,
      customHeaderStyles: rightAlignNoPaddingStyles,
      noPadding: true,
    },
  ];

  return (
    <Table<InvoiceItem> columns={columns} data={invoiceItems} autoLayout>
      <tfoot>
        <StyledFooterRow>
          <td colSpan={4} />
          <StyledFooterCell>
            {t('settings:account.billing.invoices.download.invoiceItemsTable.footers.subtotal')}
          </StyledFooterCell>
          <StyledFooterCell>
            {t('currency', {
              value: {
                value: invoice.totalAmount,
                currency_iso: invoice.totalCurrency?.iso || currencies.USD.iso,
              },
            })}
          </StyledFooterCell>
        </StyledFooterRow>
        <StyledFooterRow>
          <td colSpan={4} />
          <StyledFooterCell>
            <strong>
              {t('settings:account.billing.invoices.download.invoiceItemsTable.footers.total')}
            </strong>
          </StyledFooterCell>
          <StyledFooterCell>
            <strong>
              {t('currency', {
                value: {
                  value: invoice.totalAmount,
                  currency_iso: invoice.totalCurrency?.iso || currencies.USD.iso,
                },
              })}
            </strong>
          </StyledFooterCell>
        </StyledFooterRow>
      </tfoot>
    </Table>
  );
};

const ComposedInvoiceItemsTable = translate()(InvoiceItemsTable);

export default ComposedInvoiceItemsTable;
